import * as React from "react";
import {
  Dialog,
  NonIdealState,
  Spinner,
  OverlayToaster,
  Position,
  type OverlayToaster as OverlayToasterType,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ApiAdmin from "../../../api/admin";
import {
  FolderPermissionsForm,
  type FolderPermissionsFormProps,
} from "../../Admin/Organization/folders/FolderPermissionsDialog";
import { FolderAdminInviteUserButton } from "./FolderAdminInviteUserDialog";
import {
  serializeUserPermissionsMap,
  serializeUserPermissionsMapWithout,
} from "../../../utils/permissions";

interface FolderAdminPermissionsDialogProps {
  folder: { id: number; label: string } | null;
  onClose: () => void;
}

export function FolderAdminPermissionsDialog(props: FolderAdminPermissionsDialogProps) {
  const { folder, onClose } = props;
  const folderId = folder?.id ?? null;
  const queryClient = useQueryClient();
  const toaster = React.useRef<OverlayToasterType>(null);

  const permissionTypesQuery = useQuery({
    queryKey: ["folderAdminPermissionTypes", folderId],
    queryFn: () =>
      folderId ? ApiAdmin.getPermissionTypesForFolderAdmin(folderId) : null,
    enabled: folderId !== null,
    staleTime: 0,
    refetchOnMount: true,
  });

  const folderPermissionsQuery = useQuery({
    queryKey: ["folderAdminFolderPermissions", folderId],
    queryFn: () =>
      folderId ? ApiAdmin.getFolderPermissionsForFolderAdmin(folderId) : null,
    enabled: folderId !== null,
    staleTime: 0,
    refetchOnMount: true,
  });

  const orgUsersQuery = useQuery({
    queryKey: ["folderAdminOrgUsers", folderId],
    queryFn: () => (folderId ? ApiAdmin.getOrgUsersForFolderAdmin(folderId) : null),
    enabled: folderId !== null,
    staleTime: 0,
    refetchOnMount: true,
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: async ({
      folderId,
      userPermissions,
    }: {
      folderId: number;
      userPermissions: Map<number, number>;
    }) => {
      const user_permissions = serializeUserPermissionsMap(userPermissions);
      return ApiAdmin.updateFolderPermissionsForFolderAdmin(folderId, {
        user_permissions,
      });
    },
    onError: (err: any) => {
      toaster.current?.show({
        message: err?.message || "Failed to update permissions. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["folderAdminFolderPermissions", variables.folderId],
      });
      toaster.current?.show({
        message: "Permissions updated successfully.",
        intent: "success",
        timeout: 3000,
      });
    },
  });

  const removeUserPermissionMutation = useMutation({
    mutationFn: async ({
      folderId,
      userId,
      userPermissions,
    }: {
      folderId: number;
      userId: number;
      userPermissions: Map<number, number>;
    }) => {
      const user_permissions = serializeUserPermissionsMapWithout(userPermissions, userId);
      return ApiAdmin.updateFolderPermissionsForFolderAdmin(folderId, {
        user_permissions,
      });
    },
    onError: (err: any) => {
      toaster.current?.show({
        message: err?.message || "Failed to remove user. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["folderAdminFolderPermissions", variables.folderId],
      });
      toaster.current?.show({
        message: "User removed from folder successfully.",
        intent: "success",
        timeout: 3000,
      });
    },
  });

  const isLoading =
    permissionTypesQuery.isLoading ||
    folderPermissionsQuery.isLoading ||
    orgUsersQuery.isLoading;
  const hasError =
    permissionTypesQuery.error || folderPermissionsQuery.error || orgUsersQuery.error;

  const users = React.useMemo(
    () =>
      orgUsersQuery.data?.users?.map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
      })) ?? [],
    [orgUsersQuery.data?.users]
  );

  if (isLoading) {
    return (
      <>
        <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
        <Dialog
          className="bp6-dark"
          css={css`
            width: 600px;
          `}
          title={`Edit Folder Permissions for ${folder?.label}`}
          isOpen={folder !== null}
          onClose={onClose}
        >
          <div
            css={css`
              display: flex;
              justify-content: center;
              padding: 2rem;
            `}
          >
            <Spinner />
          </div>
        </Dialog>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
        <Dialog
          className="bp6-dark"
          css={css`
            width: 600px;
          `}
          title={`Edit Folder Permissions for ${folder?.label}`}
          isOpen={folder !== null}
          onClose={onClose}
        >
          <NonIdealState
            icon="error"
            title="Failed to Load Permissions"
            description="Please try again later."
          />
        </Dialog>
      </>
    );
  }

  const permissionTypes = permissionTypesQuery.data?.permission_types ?? [];
  const user_permissions = folderPermissionsQuery.data?.user_permissions ?? [];
  const permissions = folderPermissionsQuery.data?.permissions ?? [];

  const formProps: FolderPermissionsFormProps = {
    permissionsDialogFolder: folder ? ({ ...folder, user_ids: [], group_ids: [] } as any) : null,
    onClose,
    users,
    groups: [],
    permissionTypes,
    userPermissions: user_permissions,
    permissions,
    updatePermissionsAsync: async ({ folderId, userPermissions }) => {
      return updatePermissionsMutation.mutateAsync({ folderId, userPermissions });
    },
    isUpdatingPermissions: updatePermissionsMutation.isPending,
    removeUserPermissionAsync: async ({ folderId, userId, userPermissions }) => {
      return removeUserPermissionMutation.mutateAsync({ folderId, userId, userPermissions });
    },
    removeGroupPermissionAsync: async () => Promise.resolve(),
    isRemovingUserPermission: removeUserPermissionMutation.isPending,
    isRemovingGroupPermission: false,
    isFybrAdmin: false,
    inviteUserAction: folder
      ? () => <FolderAdminInviteUserButton folder={folder} />
      : undefined,
  };

  return (
    <>
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
      <FolderPermissionsForm {...formProps} />
    </>
  );
}

export default FolderAdminPermissionsDialog;

